import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Input, Button, Select, message } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService } from "@iso/services";
import useIsSafetyAdmin from "./shared/useIsSafetyAdmin";
import useInvestigationAccess from "./shared/useInvestigationAccess";
import useInvestigationHandlers from "./shared/useInvestigationHandlers";
import InvestigationStepper from "./shared/InvestigationStepper";
import CorrectiveActionStatus from "./shared/CorrectiveActionStatus";
import useCorrectiveActionContext, {
  todayISO,
  validateCorrectiveActions,
  validateCorrectiveActionDescriptions,
} from "./shared/correctiveActionHelpers";
import { InvestigationFormWrapper } from "./InvestigationForm.styles";

const { TextArea } = Input;
const { Option } = Select;

const STEPS = [
  { number: 1, title: "Problem Statement", description: "Define the problem" },
  { number: 2, title: "5 Whys Analysis", description: "Ask why 5 times" },
  { number: 3, title: "Root Cause", description: "Identify root cause" },
  { number: 4, title: "Corrective Actions", description: "Plan actions" },
];

const WHY_ORDINALS = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];
const getWhyLabel = (index) => WHY_ORDINALS[index] || `${index + 1}th`;
const MIN_WHYS = 5;

const DEFAULT_FORM = {
  problemStatement: "",
  whys: [
    { question: "Why did this incident occur?", answer: "" },
    { question: "", answer: "" },
    { question: "", answer: "" },
    { question: "", answer: "" },
    { question: "", answer: "" },
  ],
  rootCause: "",
  correctiveActions: [{ id: undefined, action: "", assignedTo: undefined, dueDate: "" }],
};

// Legacy shape: correctiveActions used to be an array of strings. Coerce so
// older drafts render in the new dropdown form without losing the description.
const normalizeCorrectiveAction = (ca) => {
  if (typeof ca === "string") {
    return { id: undefined, action: ca, assignedTo: undefined, dueDate: "" };
  }
  return {
    id: ca?.id,
    action: ca?.action || "",
    assignedTo: ca?.assignedTo,
    dueDate: ca?.dueDate || "",
  };
};

const parseContent = (content, incidentDescription) => {
  if (!content) {
    return { ...DEFAULT_FORM, problemStatement: incidentDescription || "" };
  }
  try {
    const parsed = JSON.parse(content);
    if (parsed && Array.isArray(parsed.whys)) {
      const merged = { ...DEFAULT_FORM, ...parsed };
      if (Array.isArray(merged.correctiveActions)) {
        merged.correctiveActions = merged.correctiveActions.map(normalizeCorrectiveAction);
      }
      return merged;
    }
    return { ...DEFAULT_FORM, problemStatement: content };
  } catch {
    return { ...DEFAULT_FORM, problemStatement: content };
  }
};

const FiveWhysInvestigation = () => {
  const { incidentId } = useParams();
  const history = useHistory();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [reviewComment, setReviewComment] = useState("");

  const isSafetyAdmin = useIsSafetyAdmin();
  const { staff, isCALocked, getLiveCA } = useCorrectiveActionContext(
    incidentId,
    incident?.property_id
  );

  useEffect(() => {
    if (incidentId) {
      loadData();
    }
  }, [incidentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentData, investigationData] = await Promise.all([
        incidentService.getById(incidentId),
        incidentService.getInvestigation(incidentId),
      ]);
      setIncident(incidentData);
      setInvestigation(investigationData);
      if (investigationData) {
        setFormData(parseContent(investigationData.content, incidentData?.description));
        setReviewComment(investigationData.review_comment || "");
      } else if (incidentData) {
        setFormData({ ...DEFAULT_FORM, problemStatement: incidentData.description || "" });
      }
    } catch (err) {
      console.error("Failed to load 5 Whys investigation data:", err);
      message.error("Failed to load investigation data.");
    } finally {
      setLoading(false);
    }
  };

  const {
    canEdit, canSubmit, isViewOnly, isApproved, isSubmitted,
  } = useInvestigationAccess(investigation, isSafetyAdmin);

  const {
    saving, submitting, approving,
    handleSaveDraft, handleSubmit, handleApprove, handleRequestMoreInfo,
  } = useInvestigationHandlers(incidentId, formData, investigation, reviewComment);

  const updateWhy = (index, field, value) => {
    const whys = [...formData.whys];
    whys[index] = { ...whys[index], [field]: value };
    setFormData({ ...formData, whys });
  };

  const addWhy = () => {
    setFormData({
      ...formData,
      whys: [...formData.whys, { question: "", answer: "" }],
    });
  };

  const removeWhy = (index) => {
    if (index < MIN_WHYS) return;
    setFormData({
      ...formData,
      whys: formData.whys.filter((_, i) => i !== index),
    });
  };

  const addCorrectiveAction = () => {
    setFormData({
      ...formData,
      correctiveActions: [
        ...formData.correctiveActions,
        { id: undefined, action: "", assignedTo: undefined, dueDate: "" },
      ],
    });
  };

  const removeCorrectiveAction = (index) => {
    const target = formData.correctiveActions[index];
    if (isCALocked(target)) {
      message.warning("Cannot remove a corrective action that has already started.");
      return;
    }
    setFormData({
      ...formData,
      correctiveActions: formData.correctiveActions.filter((_, i) => i !== index),
    });
  };

  const updateCorrectiveAction = (index, field, value) => {
    const updated = [...formData.correctiveActions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, correctiveActions: updated });
  };

  const onSubmitClick = () => {
    const error = validateCorrectiveActionDescriptions(formData.correctiveActions);
    if (error) {
      message.error(error);
      setCurrentStep(4);
      return;
    }
    handleSubmit();
  };

  const onApproveClick = () => {
    const error = validateCorrectiveActions(formData.correctiveActions);
    if (error) {
      setCurrentStep(4);
      message.error(error);
      return;
    }
    handleApprove(formData.correctiveActions);
  };

  // Approver sets these fields at the approval step (Mary 2026-05-06).
  // Investigator only authors descriptions — surface the assignee + due date
  // inputs once the investigation has left their hands (review/approved) or
  // when the current viewer is the Safety Admin doing the approval.
  const showApprovalFields = isSafetyAdmin || isSubmitted || isApproved;
  const approvalFieldsLocked = !isSafetyAdmin || isApproved;

  if (loading) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="loading-container"><Spin size="large" /></div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  if (!incident) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="not-found-container">
              <h2 className="not-found-title">Incident not found</h2>
              <span className="not-found-link" onClick={() => history.push("/incidents")}>
                Return to incidents
              </span>
            </div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationFormWrapper>
          {/* Header */}
          <div className="inv-header">
            <div>
              <button className="inv-back-button" onClick={() => history.push("/incidents")}>
                <ArrowLeftOutlined /> Back to Incidents
              </button>
              <h1 className="inv-title">5 Whys Investigation</h1>
              <p className="inv-subtitle">
                {incident.report_number} — {incident.description || ""}
              </p>
            </div>
            <div className="inv-header-actions">
              {canEdit && (
                <Button className="btn-save-draft" onClick={handleSaveDraft} loading={saving}>
                  <SaveOutlined /> Save Draft
                </Button>
              )}
              {canSubmit && (
                <Button type="primary" className="btn-submit" onClick={onSubmitClick} loading={submitting}>
                  <SendOutlined /> Submit Investigation
                </Button>
              )}
            </div>
          </div>

          {/* Approved Notice */}
          {isApproved && (
            <div className="readonly-notice">
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              This investigation has been approved and is read-only.
            </div>
          )}

          {/* View-Only Notice */}
          {isViewOnly && !isApproved && (
            <div className="readonly-notice" style={{ background: "#eff6ff", borderColor: "#bfdbfe", color: "#1e40af" }}>
              You are viewing this investigation in read-only mode. Only the assigned investigator and Safety Admins can make changes.
            </div>
          )}

          {/* Safety Admin Review Section */}
          {isSafetyAdmin && isSubmitted && (
            <div className="review-section">
              <h3 className="review-title">Safety Admin Review</h3>
              <p className="review-status">This investigation has been submitted and is awaiting your review.</p>
              <TextArea
                rows={3}
                placeholder="Add a review comment (optional)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <div className="review-actions">
                <Button
                  className="btn-approve"
                  type="primary"
                  onClick={onApproveClick}
                  loading={approving}
                >
                  Approve Investigation
                </Button>
                <Button className="btn-request-info" onClick={handleRequestMoreInfo} loading={approving}>
                  Request More Info
                </Button>
              </div>
            </div>
          )}

          <InvestigationStepper steps={STEPS} currentStep={currentStep} onStepClick={setCurrentStep} />

          {/* Form Content */}
          <div className="form-card">
            {/* Step 1: Problem Statement */}
            {currentStep === 1 && (
              <div>
                <h2 className="step-title">Step 1: Define the Problem Statement</h2>
                <p className="step-description">
                  Clearly describe what happened. Be specific and factual. This should be a concise statement of the problem.
                </p>
                <TextArea
                  rows={5}
                  value={formData.problemStatement}
                  onChange={(e) => setFormData({ ...formData, problemStatement: e.target.value })}
                  placeholder="Example: Gate damaged, livestock escaped into neighboring property"
                  disabled={!canEdit}
                />
                <div className="tips-box tips-box-blue">
                  <p className="tips-box-title">Tips for a good problem statement:</p>
                  <ul className="tips-box-list">
                    <li>Be specific and avoid vague language</li>
                    <li>Focus on what happened, not why it happened</li>
                    <li>Include measurable impacts if applicable</li>
                    <li>Avoid blame or assumptions</li>
                  </ul>
                </div>
                <div className="step-nav" style={{ justifyContent: "flex-end" }}>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(2)}>
                    Continue to 5 Whys Analysis
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: 5 Whys Analysis */}
            {currentStep === 2 && (
              <div>
                <h2 className="step-title">Step 2: 5 Whys Analysis</h2>
                <p className="step-description">
                  Ask "why" iteratively to drill down to the root cause. Each answer becomes the basis for the next question.
                </p>

                {formData.whys.map((why, index) => (
                  <div className="why-card" key={index}>
                    <div className="why-card-header">
                      <div className="why-number">{index + 1}</div>
                      <span className="why-title">{getWhyLabel(index)} Why</span>
                      {canEdit && index >= MIN_WHYS && (
                        <button
                          className="btn-delete-icon"
                          onClick={() => removeWhy(index)}
                          style={{ marginLeft: "auto" }}
                        >
                          <DeleteOutlined />
                        </button>
                      )}
                    </div>
                    <label className="field-label-sm">Question</label>
                    <Input
                      value={why.question}
                      onChange={(e) => updateWhy(index, "question", e.target.value)}
                      placeholder={index === 0 ? "Why did this incident occur?" : "Why did [previous answer] happen?"}
                      disabled={!canEdit}
                      style={{ marginBottom: 12 }}
                    />
                    <label className="field-label-sm">Answer</label>
                    <TextArea
                      rows={3}
                      value={why.answer}
                      onChange={(e) => updateWhy(index, "answer", e.target.value)}
                      placeholder="Enter your answer..."
                      disabled={!canEdit}
                    />
                  </div>
                ))}

                {canEdit && (
                  <Button icon={<PlusOutlined />} onClick={addWhy} style={{ marginBottom: 24 }}>
                    Add Another Why
                  </Button>
                )}

                <div className="step-nav">
                  <Button onClick={() => setCurrentStep(1)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(3)}>
                    Continue to Root Cause
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Root Cause */}
            {currentStep === 3 && (
              <div>
                <h2 className="step-title">Step 3: Identify the Root Cause</h2>
                <p className="step-description">
                  Based on your 5 Whys analysis, summarize the root cause of the incident. This should address the underlying systemic issue.
                </p>
                <TextArea
                  rows={6}
                  value={formData.rootCause}
                  onChange={(e) => setFormData({ ...formData, rootCause: e.target.value })}
                  placeholder="Summarize the root cause based on your analysis..."
                  disabled={!canEdit}
                />
                <div className="tips-box tips-box-yellow">
                  <p className="tips-box-title">Remember:</p>
                  <ul className="tips-box-list">
                    <li>The root cause should be actionable</li>
                    <li>It should address systemic issues, not just symptoms</li>
                    <li>Consider organizational, procedural, and human factors</li>
                  </ul>
                </div>
                <div className="step-nav">
                  <Button onClick={() => setCurrentStep(2)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(4)}>
                    Continue to Corrective Actions
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Corrective Actions */}
            {currentStep === 4 && (
              <div>
                <h2 className="step-title">Step 4: Corrective Actions</h2>
                <p className="step-description">
                  Define specific actions to address the root cause and prevent recurrence. Be specific and actionable.
                </p>

                {formData.correctiveActions.map((storedAction, index) => {
                  // Render live values for actions that already exist in the
                  // Corrective Actions module — completion, reallocation and a
                  // revised due date all land there, not in this JSON snapshot.
                  const action = getLiveCA(storedAction);
                  const locked = isCALocked(action);
                  const fieldDisabled = !canEdit || locked;
                  return (
                    <div className="action-card" key={index}>
                      <div className="action-card-header">
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#101828" }}>
                          Corrective Action {index + 1}
                        </span>
                        {canEdit && formData.correctiveActions.length > 1 && !locked && (
                          <button
                            className="btn-delete-icon"
                            onClick={() => removeCorrectiveAction(index)}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                      <CorrectiveActionStatus action={action} />
                      <label className="field-label-sm">Action Description</label>
                      <TextArea
                        rows={3}
                        value={action.action}
                        onChange={(e) => updateCorrectiveAction(index, "action", e.target.value)}
                        placeholder="Describe the corrective action in detail..."
                        disabled={fieldDisabled}
                        style={{ marginBottom: 16 }}
                      />
                      {showApprovalFields && (
                        <div className="action-grid">
                          <div>
                            <label className="field-label-sm">Assignee</label>
                            <Select
                              style={{ width: "100%" }}
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                (option?.children || "")
                                  .toString()
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              value={action.assignedTo || undefined}
                              onChange={(val) => updateCorrectiveAction(index, "assignedTo", val)}
                              placeholder="Select staff member"
                              disabled={approvalFieldsLocked || locked}
                            >
                              {staff.map((u) => (
                                <Option key={u.id} value={u.id}>
                                  {u.name || u.full_name || u.email}
                                </Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <label className="field-label-sm">Due Date</label>
                            <Input
                              type="date"
                              min={todayISO()}
                              value={action.dueDate}
                              status={action.dueDate && action.dueDate < todayISO() ? "error" : ""}
                              onChange={(e) => updateCorrectiveAction(index, "dueDate", e.target.value)}
                              disabled={approvalFieldsLocked || locked}
                            />
                            {action.dueDate && action.dueDate < todayISO() && (
                              <span style={{ color: "#ff4d4f", fontSize: 12 }}>Due date cannot be in the past.</span>
                            )}
                          </div>
                        </div>
                      )}
                      {!showApprovalFields && (
                        <p className="step-description" style={{ marginTop: 0, fontSize: 12, color: "#667085" }}>
                          The Safety Admin will assign this action and set the due date at the approval step.
                        </p>
                      )}
                    </div>
                  );
                })}

                {canEdit && (
                  <Button icon={<PlusOutlined />} onClick={addCorrectiveAction} style={{ marginBottom: 24 }}>
                    Add Another Action
                  </Button>
                )}

                <div className="step-nav">
                  <Button onClick={() => setCurrentStep(3)}>Back</Button>
                  <div className="step-nav-end">
                    {canEdit && (
                      <Button className="btn-save-draft" onClick={handleSaveDraft} loading={saving}>
                        <SaveOutlined /> Save Draft
                      </Button>
                    )}
                    {canSubmit && (
                      <Button type="primary" className="btn-submit" onClick={onSubmitClick} loading={submitting}>
                        <SendOutlined /> Submit Investigation
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </InvestigationFormWrapper>
      </Box>
    </LayoutWrapper>
  );
};

export default FiveWhysInvestigation;
